import { useRef, useMemo, ReactNode } from 'react';
import goldMedal from '../assets/images/common/gold_medal.svg';
import silverMedal from '../assets/images/common/silver_medal.svg';
import bronzeMedal from '../assets/images/common/bronze_medal.svg';
import { getHoursAndMinutes, getWeekday } from '../utils/time';
import PeriodComparison from '@/components/PeriodComparison';
import { PeriodComparison as PeriodComparisonType } from '@/types/AnalysisResultType';
import { useResponsiveFontSize } from '@/hooks/useResponsiveFontSize';
import { useTranslation } from 'react-i18next';

interface RankingItem {
    title: string;
    percentage: number;
    totalTime: number;
    timeComparison?: PeriodComparisonType;
}

/**
 * ランキング
 * @param rankingItems ランキングアイテム
 * @returns ランキングセクション
 **/
export const Ranking = ({ rankingItems }: { rankingItems: RankingItem[] }) => {
    // レスポンシブ対応
    const containerRef = useRef<HTMLDivElement>(null);
    // 動的なフォントサイズを取得
    const fontSize = useResponsiveFontSize(containerRef);
    const { t, i18n } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');
    const rankingTitle = useMemo(() => t('ranking.title'), [t]);
    const noDataLabel = useMemo(() => t('ranking.noData'), [t]);
    const timeJoiner = useMemo(
        () =>
            tCommon('time.joiner', {
                defaultValue: i18n.language.startsWith('ja') ? '' : ' ',
            }),
        [i18n.language, tCommon],
    );

    const formatTotalTime = (totalMinutes: number) => {
        const [hours, minutes] = getHoursAndMinutes(totalMinutes);
        if (i18n.language.startsWith('en')) {
            const segments: ReactNode[] = [];
            const valueStyle = { fontSize: '0.9em', lineHeight: '1' } as const;
            const unitStyle = { fontSize: '0.7em', marginLeft: '0.15em' } as const;

            if (hours > 0) {
                segments.push(
                    <span key="h-value" style={valueStyle}>
                        {hours}
                    </span>,
                    <span key="h-unit" className="text-gray-500" style={unitStyle}>
                        h
                    </span>,
                );
            }

            segments.push(
                <span key="m-value" style={valueStyle}>
                    {minutes}
                </span>,
                <span key="m-unit" className="text-gray-500" style={unitStyle}>
                    m
                </span>,
            );

            return <>{segments}</>;
        }

        const parts: string[] = [];
        if (hours > 0) {
            parts.push(tCommon('time.hours', { count: hours }));
        }
        if (minutes > 0 || parts.length === 0) {
            parts.push(tCommon('time.minutes', { count: minutes }));
        }
        return parts.join(timeJoiner);
    };

    const tightenSlash = (value: string) => value.replace(/\s*\/\s*/g, '/');

    // データが存在しない場合
    if (!rankingItems?.length) {
        return (
            <div className="items-center justify-center text-center">
                <p className="text-xl">{rankingTitle}</p>
                <br />
                <p className="">{noDataLabel}</p>
            </div>
        );
    }

    // ランキングアイテムの設定
    const rankConfig = [
        { medal: goldMedal, maxWidth: '2em', textSize: '1.2em' },
        { medal: silverMedal, maxWidth: '1.8em', textSize: '1em' },
        { medal: bronzeMedal, maxWidth: '1.7em', textSize: '0.9em' },
    ];

    return (
        <div
            ref={containerRef}
            style={{ fontSize: `${fontSize}px` }}
            className="w-full h-full flex flex-col justify-center items-center text-center"
        >
            <p className="text-lg mx-auto mb-2" style={{ fontSize: '1em' }}>
                {rankingTitle}
            </p>
            {rankingItems.map((item, index) => {
                // 2位、3位は totalTime が 0 の場合は表示しない
                if (index > 0 && item.totalTime === 0) return null;

                const config = rankConfig[index];
                const rank = index + 1;
                // 上位3位まで ＆ 前期データがある場合に、前期比表示
                const shouldShowComparison = rank <= 3 && item.timeComparison;

                // タイトルの整形
                let formattedTitle = item.title;
                // 日付形式の場合は曜日を付与
                const dateRegex = /^\d{1,2}\/\d{1,2}$/;
                const fullDateRegex = /^\d{4}[\/-]\d{1,2}[\/-]\d{1,2}$/;
                let dateForWeekday = '';
                if (dateRegex.test(item.title)) {
                    const year = new Date().getFullYear();
                    dateForWeekday = `${year}/${item.title}`;
                } else if (fullDateRegex.test(item.title)) {
                    dateForWeekday = item.title.replace(/-/g, '/');
                    if (i18n.language.startsWith('en')) {
                        formattedTitle = dateForWeekday;
                    }
                }
                if (dateForWeekday) {
                    // 曜日は省略表記
                    const weekday = getWeekday(dateForWeekday, i18n.language);
                    if (weekday) {
                        formattedTitle = i18n.language.startsWith('en')
                            ? `${tightenSlash(formattedTitle)}(${weekday})`
                            : `${formattedTitle}(${weekday})`;
                    }
                } else if (i18n.language.startsWith('en')) {
                    formattedTitle = tightenSlash(formattedTitle);
                }
                // 省略はせずそのまま表示

                return (
                    <div
                        key={index}
                        className="flex w-11/12 lg:w-9/12 items-center"
                        style={{ height: '4.8em' }}
                    >
                        {/* メダル */}
                        <div className="flex w-2/12 items-center justify-center ">
                            <img
                                src={config.medal}
                                alt="medal"
                                className="w-full h-auto image-protection"
                                style={{ maxWidth: config.maxWidth }}
                            />
                        </div>
                        {/* タイトルと時間 */}
                        <div className={`px-0 py-2 ms-4 text-left border-b pt-0`}>
                            <p className={`m-0`} style={{ fontSize: `${config.textSize}` }}>
                                {formattedTitle + ' '}
                                <span
                                    className={`text-gray-500 pl-1`}
                                    style={{ fontSize: `${index === 0 ? '0.8em' : '0.8em'}` }}
                                >
                                    {/* percentageが0の場合は表示しない */}
                                    {item.percentage === 0 ? '' : item.percentage + '% '}
                                    {/* 時間が0の場合でも単位付きで表示 */}
                                    <span
                                        style={{
                                            fontSize: i18n.language.startsWith('en')
                                                ? '0.75em'
                                                : '0.9em',
                                        }}
                                    >
                                        {formatTotalTime(item.totalTime)}
                                    </span>
                                </span>
                            </p>
                            {/* 前期比表示（上位3位まで） */}
                            {shouldShowComparison &&
                                (item.timeComparison &&
                                item.timeComparison.previousValue !== undefined &&
                                item.timeComparison.previousValue !== null ? (
                                    <PeriodComparison
                                        comparison={item.timeComparison}
                                        valueType="time"
                                        className="mt-1 text-sm"
                                    />
                                ) : shouldShowComparison ? (
                                    <span className="text-gray-400 text-sm mt-1 block">-</span>
                                ) : null)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Ranking;

export {};
